import React, { useState, useEffect } from "react"
import { Tooltip, Radio, RadioChangeEvent } from "antd"
import { useDataImport } from "features/DataImport/api/useDataImport"

import DataImportTable from "features/DataImport/components/DataImportTable/DataImportTable.component"
import DataQButton from "shared/ui/Buttons/DataQButton.component"
import ProgressModal from "shared/ui/ProgressModal/ProgressModal.component"

import { useProjectStore } from "stores/projectStore"
import {
  DATAFILE_TYPE,
  DataFileItem,
  CombineDataFilesResponse,
  ImportMethod,
} from "types/dataImport.types"

import { notify } from "shared/ui/Notifications/notify"

import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import {
  faCircleExclamation,
  faTriangleExclamation,
  faXmark,
  faCheckCircle,
} from "@fortawesome/free-solid-svg-icons"

/**
 * DataImportContainer component handles the logic for checking and combining data files.
 * It interacts with various hooks and components to ensure data is correctly imported and combined.
 *
 * @component
 * @returns  The rendered DataImportContainer component.
 */
const DataImportContainer: React.FC = () => {
  const project = useProjectStore((state) => state.project)
  const {
    getDataFilesUploaded,
    isGettingDataFilesUploaded,
    combineDataFiles,
    isCombiningDataFiles,
    generateDQDFromJSON,
    isGeneratingDQD,
  } = useDataImport()
  const [dataFiles, setDataFiles] = useState<DataFileItem[]>([])
  const [importWarnings, setImportWarnings] = useState<string[]>([])
  const [importMethod, setImportMethod] = useState<ImportMethod>("csv_map")

  /**
   * useEffect hook that triggers the checkDataFiles function when the component mounts.
   */
  useEffect(() => {
    checkDataFiles()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  /**
   * Function to check if data files have been uploaded for the current project.
   * If successful, it updates the state with the data files.
   * In case of an error, it displays an error notification.
   *
   * @async
   * @function
   */
  const checkDataFiles = async (): Promise<void> => {
    try {
      const csvFileRes = await getDataFilesUploaded(
        project.id,
        project.Created_by,
        DATAFILE_TYPE.CSV,
        "size"
      )
      const mapFileRes = await getDataFilesUploaded(
        project.id,
        project.Created_by,
        DATAFILE_TYPE.MAP,
        "size"
      )
      const jsonFileRes = await getDataFilesUploaded(
        project.id,
        project.Created_by,
        DATAFILE_TYPE.JSON,
        "size"
      )
      const dqdFileRes = await getDataFilesUploaded(
        project.id,
        project.Created_by,
        DATAFILE_TYPE.DQD,
        "size"
      )

      const dataFiles: DataFileItem[] = [
        {
          key: "data",
          fileType: "Data File (.data.csv)",
          isUploaded: typeof csvFileRes.data === "number",
          type: DATAFILE_TYPE.CSV,
        },
        {
          key: "map",
          fileType: "Map File (.map.csv)",
          isUploaded: typeof mapFileRes.data === "number",
          type: DATAFILE_TYPE.MAP,
        },
        {
          key: "json",
          fileType: "JSON Data File (.json)",
          isUploaded: typeof jsonFileRes.data === "number",
          type: DATAFILE_TYPE.JSON,
        },
        {
          key: "result",
          fileType: "Data(Q) Data file (.dqd)",
          isUploaded: typeof dqdFileRes.data === "number",
          type: DATAFILE_TYPE.DQD,
        },
      ]

      setDataFiles(dataFiles || [])
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : "Unknown error occurred"
      notify.error("Error checking data files!", errorMessage)
    }
  }

  /**
   * Function to combine the data files if both required files (.data.csv and .map.csv) are present.
   * Displays a success notification on successful combination, otherwise displays an error notification.
   * Also extracts and displays any import warnings.
   *
   * @async
   * @function
   */
  const combineDataWithMap = async (): Promise<void> => {
    try {
      const combineRes = (await combineDataFiles(project.id)) as CombineDataFilesResponse

      // Handle different response formats
      let responseMessage: string | undefined
      let warnings: string | undefined

      if (typeof combineRes.data === "string") {
        // Legacy format: plain string response
        responseMessage = combineRes.data
      } else if (combineRes.data?.result) {
        // Wrapped format: { result: { message, warnings } }
        responseMessage = combineRes.data.result.message
        warnings = combineRes.data.result.warnings
      } else {
        // Direct format: { message, warnings }
        responseMessage = combineRes.data?.message
        warnings = combineRes.data?.warnings
      }

      // Extract warnings into array (split by newlines)
      if (warnings) {
        const warningsList = warnings.split("\n").filter((w) => w.trim() !== "")
        setImportWarnings(warningsList)
      } else {
        setImportWarnings([])
      }

      if (responseMessage === "Data Combined Successfully!") {
        notify.toast.success(warnings ? "Data combined - see warnings below" : "Data combined successfully!")
        checkDataFiles()
      } else {
        notify.error("Error combining data files!", String(combineRes))
        checkDataFiles()
      }
    } catch (combineError) {
      const errorMessage =
        combineError instanceof Error
          ? combineError.message
          : "Unknown error occurred"
      notify.error("Error combining data files!", errorMessage)
    }
  }

  /**
   * Function to generate a DQD file from an uploaded JSON data file.
   * Displays a success notification on successful generation, otherwise displays an error notification.
   *
   * @async
   * @function
   */
  const generateDQD = async (): Promise<void> => {
    try {
      const generateRes = (await generateDQDFromJSON(project.id)) as CombineDataFilesResponse

      // Handle different response formats
      let responseMessage: string | undefined

      if (typeof generateRes.data === "string") {
        responseMessage = generateRes.data
      } else if (generateRes.data?.result) {
        responseMessage = generateRes.data.result.message
      } else {
        responseMessage = generateRes.data?.message
      }

      if (responseMessage === "DQD file generated successfully from JSON!") {
        notify.toast.success("DQD file generated successfully!")
        checkDataFiles()
      } else {
        notify.error("Error generating DQD file!", String(generateRes))
        checkDataFiles()
      }
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : "Unknown error occurred"
      notify.error("Error generating DQD file!", errorMessage)
    }
  }

  const hasCsvAndMap =
    dataFiles.some(
      (file) => file.type === DATAFILE_TYPE.CSV && file.isUploaded
    ) &&
    dataFiles.some((file) => file.type === DATAFILE_TYPE.MAP && file.isUploaded)

  const hasJsonFile = dataFiles.some(
    (file) => file.type === DATAFILE_TYPE.JSON && file.isUploaded
  )

  const hasDqdFile = dataFiles.some(
    (file) => file.type === DATAFILE_TYPE.DQD && file.isUploaded
  )

  const isProcessing = isCombiningDataFiles || isGeneratingDQD

  // Filter data files based on selected import method
  const filteredDataFiles = dataFiles.filter((file) => {
    switch (importMethod) {
      case "csv_map":
        return file.type === DATAFILE_TYPE.CSV || file.type === DATAFILE_TYPE.MAP
      case "json":
        return file.type === DATAFILE_TYPE.JSON
      case "dqd":
        return file.type === DATAFILE_TYPE.DQD
      default:
        return true
    }
  })

  const handleImportMethodChange = (e: RadioChangeEvent) => {
    setImportMethod(e.target.value as ImportMethod)
    setImportWarnings([]) // Clear warnings when switching methods
  }

  return (
    <>
      <ProgressModal
        title={isGeneratingDQD ? "Generating DQD File" : "Combining Data Files"}
        isLoading={isProcessing}
        duration={3200}
      />
      <div className="flex flex-col h-full overflow-y-auto w-full">
        {/* Import Method Selection */}
        <div className="p-4 border-b border-gray-200">
          <p className="text-sm text-gray-600 mb-2">Choose your import method:</p>
          <Radio.Group
            value={importMethod}
            onChange={handleImportMethodChange}
            optionType="button"
            buttonStyle="solid"
            className="dataq-radio-group"
          >
            <Radio.Button value="csv_map">
              CSV + Map Files
            </Radio.Button>
            <Radio.Button value="json">
              JSON Data File
            </Radio.Button>
            <Radio.Button value="dqd">
              DQD File (Direct)
            </Radio.Button>
          </Radio.Group>
          <p className="text-xs text-gray-400 mt-2">
            {importMethod === "csv_map" && "Upload a data file (.data.csv) and map file (.map.csv), then combine them to create a DQD file."}
            {importMethod === "json" && "Upload a Data(Q) JSON file and convert it to DQD format."}
            {importMethod === "dqd" && "Upload a DQD file directly if you already have one."}
          </p>
        </div>

        {/* File Upload Table - filtered by selected method */}
        <DataImportTable
          refresh={checkDataFiles}
          isLoading={isGettingDataFilesUploaded}
          dataFiles={filteredDataFiles}
        />

        {/* Show Combine button for CSV + Map method */}
        {importMethod === "csv_map" && !hasDqdFile && hasCsvAndMap && (
          <div className="flex p-5 ml-auto">
            <DataQButton type="primary" onClick={combineDataWithMap}>
              Combine Data Files
            </DataQButton>
            <Tooltip
              title="Combine the data and map files to generate the Data(Q) data file."
              color={"black"}
              className="ml-2 my-auto"
            >
              <FontAwesomeIcon
                icon={faCircleExclamation}
                color="gold"
                className="cursor-pointer"
              />
            </Tooltip>
          </div>
        )}

        {/* Show Generate DQD button for JSON method */}
        {importMethod === "json" && !hasDqdFile && hasJsonFile && (
          <div className="flex p-5 ml-auto">
            <DataQButton type="primary" onClick={generateDQD}>
              Generate DQD
            </DataQButton>
            <Tooltip
              title="Convert the JSON data file to Data(Q) DQD format."
              color={"black"}
              className="ml-2 my-auto"
            >
              <FontAwesomeIcon
                icon={faCircleExclamation}
                color="gold"
                className="cursor-pointer"
              />
            </Tooltip>
          </div>
        )}

        {/* Show success message when DQD is ready */}
        {hasDqdFile && (
          <div className="mx-4 mt-4">
            <div className="flex items-center gap-3 px-4 py-3 bg-emerald-50 border border-emerald-200 rounded-lg">
              <FontAwesomeIcon
                icon={faCheckCircle}
                className="text-emerald-500 text-lg"
              />
              <div>
                <p className="text-sm font-medium text-emerald-800 m-0">Data Ready</p>
                <p className="text-xs text-emerald-600 m-0">Your DQD file is uploaded and ready for reporting.</p>
              </div>
            </div>
          </div>
        )}

        {/* Import Warnings Panel */}
        {importWarnings.length > 0 && (
          <div className="mx-4 mt-4">
            <div className="bg-amber-50 border border-amber-200 rounded-lg overflow-hidden">
              <div className="flex items-center justify-between px-4 py-3 bg-amber-100/50 border-b border-amber-200">
                <div className="flex items-center gap-2">
                  <FontAwesomeIcon
                    icon={faTriangleExclamation}
                    className="text-amber-500"
                  />
                  <span className="text-sm font-medium text-amber-800">
                    Import Warnings ({importWarnings.length})
                  </span>
                </div>
                <button
                  onClick={() => setImportWarnings([])}
                  className="text-amber-600 hover:text-amber-800 transition-colors p-1"
                >
                  <FontAwesomeIcon icon={faXmark} />
                </button>
              </div>
              <div className="max-h-48 overflow-y-auto p-3">
                <div className="space-y-1.5">
                  {importWarnings.map((warning, index) => (
                    <div
                      key={index}
                      className="text-sm text-amber-700 pl-3 border-l-2 border-amber-300 py-0.5"
                    >
                      {warning}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  )
}

export default DataImportContainer
